import { createTheme } from '@mui/material/styles';

// Brand color palette
const brandColors = {
  primary: {
    main: '#112d4e',
    light: '#1a4370',
    dark: '#0d1f35',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#3f72af',
    light: '#5a8bc9',
    dark: '#2d5380',
    contrastText: '#ffffff',
  },
  accent: {
    main: '#667eea',
    light: '#8a9ff0',
    dark: '#4d5fc7',
  },
  neutral: {
    50: '#f8f9fa',
    100: '#f0f8ff',
    200: '#e9ecef',
    300: '#dee2e6',
    400: '#ced4da',
    500: '#adb5bd',
    600: '#6c757d',
    700: '#495057',
    800: '#343a40',
    900: '#212529',
  },
};

// Create MUI theme
const theme = createTheme({
  palette: {
    primary: {
      main: brandColors.primary.main,
      light: brandColors.primary.light,
      dark: brandColors.primary.dark,
      contrastText: brandColors.primary.contrastText,
    },
    secondary: {
      main: brandColors.secondary.main,
      light: brandColors.secondary.light,
      dark: brandColors.secondary.dark,
      contrastText: brandColors.secondary.contrastText,
    },
    accent: {
      main: brandColors.accent.main,
      light: brandColors.accent.light,
      dark: brandColors.accent.dark,
    },
    background: {
      default: '#f8f9fa',
      paper: '#ffffff',
      neutral: brandColors.neutral[100],
    },
    text: {
      primary: brandColors.neutral[900],
      secondary: brandColors.neutral[700],
      disabled: brandColors.neutral[500],
    },
    divider: brandColors.neutral[300],
    error: {
      main: '#dc3545',
      light: '#ea6978',
      dark: '#bd2130',
    },
    warning: {
      main: '#ffc107',
      light: '#ffd451',
      dark: '#c79100',
    },
    info: {
      main: '#17a2b8',
      light: '#45b5c6',
      dark: '#117a8b',
    },
    success: {
      main: '#198754',
      light: '#47a176',
      dark: '#126d43',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    h1: {
      fontWeight: 600,
      fontSize: '2.5rem',
      lineHeight: 1.2,
      color: brandColors.primary.main,
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
      lineHeight: 1.3,
      color: brandColors.primary.main,
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      lineHeight: 1.4,
      color: brandColors.primary.main,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
      color: brandColors.primary.main,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.5,
      color: brandColors.primary.main,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.6,
      color: brandColors.primary.main,
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0 1px 3px rgba(0,0,0,0.05)',
    '0 2px 4px rgba(0,0,0,0.06)',
    '0 3px 6px rgba(0,0,0,0.08)',
    '0 4px 8px rgba(0,0,0,0.1)',
    '0 6px 12px rgba(0,0,0,0.12)',
    '0 8px 16px rgba(0,0,0,0.14)',
    '0 10px 20px rgba(0,0,0,0.16)',
    '0 12px 24px rgba(0,0,0,0.18)',
    '0 14px 28px rgba(0,0,0,0.2)',
    '0 16px 32px rgba(0,0,0,0.22)',
    '0 18px 36px rgba(0,0,0,0.24)',
    '0 20px 40px rgba(0,0,0,0.26)',
    '0 22px 44px rgba(0,0,0,0.28)',
    '0 24px 48px rgba(0,0,0,0.3)',
    '0 26px 52px rgba(0,0,0,0.32)',
    '0 28px 56px rgba(0,0,0,0.34)',
    '0 30px 60px rgba(0,0,0,0.36)',
    '0 32px 64px rgba(0,0,0,0.38)',
    '0 34px 68px rgba(0,0,0,0.4)',
    '0 36px 72px rgba(0,0,0,0.42)',
    '0 38px 76px rgba(0,0,0,0.44)',
    '0 40px 80px rgba(0,0,0,0.46)',
    '0 42px 84px rgba(0,0,0,0.48)',
    '0 44px 88px rgba(0,0,0,0.5)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: 8,
          padding: '8px 16px',
        },
        contained: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        elevation1: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.06)',
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: `1px solid ${brandColors.neutral[300]}`,
          borderRadius: 8,
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: brandColors.primary.main,
            color: '#ffffff',
            fontWeight: 600,
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 600,
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: brandColors.neutral[50],
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          backgroundColor: brandColors.primary.main,
          color: '#ffffff',
          fontWeight: 600,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
              borderColor: brandColors.primary.light,
            },
            '&.Mui-focused fieldset': {
              borderColor: brandColors.primary.main,
            },
          },
        },
      },
    },
  },
});

export default theme;
export { brandColors };
